import { useMemo } from 'react'
import SummaryCard from 'src/components/analytics/common/SummaryCard'
import { useChartData } from 'src/components/overview/useChartData'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { WidgetId, WidgetSections } from 'src/lib/contexts/AnalyticsContext/types'
import { formatNumberToTwoDecimals, percentageWithFixedDecmials } from 'src/lib/utils/analytics'

const OverviewSection = () => {
  const {
    getters: { refreshKey, widgetConfig }
  } = useAnalytics()
  const { ref, isLoading, isError, data } = useChartData({
    chartConfig: {
      id: 'summary',
      dependencies: {
        endpoints: ['/conversion-rate', '/order-details', '/prepaid-share']
      },
      isNoComparison: false
    },
    refreshKey
  })

  const summaryData: {
    conversionRate: number
    conversionRateComparedPercentage: number
    grossRevenue: number
    grossRevenueComparedPercentage: number
    averageOrderValue: number
    averageOrderValueComparedPercentage: number
    totalOrders: number
    totalOrdersComparedPercentage: number
    prepaidShare: number
    prepaidShareCompared: number
    prepaidShareComparedPercentage: number
  } = useMemo(() => {
    const conversionRateData = formatNumberToTwoDecimals(data[0]?.data?.result?.widgetData?.data || 0)
    const conversionRateComparedData = formatNumberToTwoDecimals(
      data[0]?.data?.result?.widgetData?.compared_data?.toFixed(2) || 0
    )

    const grossRevenueData = formatNumberToTwoDecimals(data[1]?.data?.result?.widgetData?.data?.gross_revenue || 0)
    const grossRevenueComparedData = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.compared_data?.gross_revenue || 0
    )

    const averageOrderValueData = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.data?.average_order_value || 0
    )
    const averageOrderValueComparedData = formatNumberToTwoDecimals(
      data[1]?.data?.result?.widgetData?.compared_data?.average_order_value || 0
    )

    const totalOrdersData = data[1]?.data?.result?.widgetData?.data?.total_orders || 0
    const totalOrdersComparedData = data[1]?.data?.result?.widgetData?.compared_data?.total_orders || 0

    const prepaidShareData = formatNumberToTwoDecimals(data[2]?.data?.result?.widgetData?.data || 0)
    const prepaidShareComparedData = formatNumberToTwoDecimals(
      data[2]?.data?.result?.widgetData?.compared_data?.toFixed(2) || 0
    )

    return {
      conversionRate: conversionRateData,
      conversionRateComparedPercentage:
        conversionRateComparedData > 0 ? formatNumberToTwoDecimals(conversionRateData - conversionRateComparedData) : 0,
      grossRevenue: grossRevenueData,
      grossRevenueComparedPercentage: percentageWithFixedDecmials(grossRevenueData, grossRevenueComparedData),
      averageOrderValue: averageOrderValueData,
      averageOrderValueComparedPercentage: percentageWithFixedDecmials(
        averageOrderValueData,
        averageOrderValueComparedData
      ),
      totalOrders: totalOrdersData,
      totalOrdersComparedPercentage: percentageWithFixedDecmials(totalOrdersData, totalOrdersComparedData),
      prepaidShare: prepaidShareData,
      prepaidShareCompared:
        prepaidShareComparedData > 0 ? formatNumberToTwoDecimals(prepaidShareData - prepaidShareComparedData) : 0,
      prepaidShareComparedPercentage: percentageWithFixedDecmials(prepaidShareData, prepaidShareComparedData)
    }
  }, [data])

  const summaryWidgets = widgetConfig[WidgetSections.summary]

  const summaryCards = useMemo(() => {
    return [
      {
        title: summaryWidgets?.[WidgetId.summary_conversion_rate]?.widget_title,
        value: summaryData.conversionRate,
        change: summaryData.conversionRateComparedPercentage,
        description: summaryWidgets?.[WidgetId.summary_conversion_rate]?.tool_tip.primary_text,
        isPercentage: true
      },
      {
        title: summaryWidgets?.[WidgetId.summary_net_sales]?.widget_title,
        value: summaryData.grossRevenue,
        change: summaryData.grossRevenueComparedPercentage,
        isRupee: true,
        description: summaryWidgets?.[WidgetId.summary_net_sales]?.tool_tip.primary_text
      },
      {
        title: summaryWidgets?.[WidgetId.summary_aov]?.widget_title,
        value: summaryData.averageOrderValue,
        change: summaryData.averageOrderValueComparedPercentage,
        isRupee: true,
        description: summaryWidgets?.[WidgetId.summary_aov]?.tool_tip.primary_text
      },
      {
        title: summaryWidgets?.[WidgetId.summary_orders]?.widget_title,
        value: summaryData.totalOrders,
        change: summaryData.totalOrdersComparedPercentage,
        description: summaryWidgets?.[WidgetId.summary_orders]?.tool_tip.primary_text
      },
      {
        title: summaryWidgets?.[WidgetId.summary_prepaid_share]?.widget_title,
        value: summaryData.prepaidShare,
        change: summaryData.prepaidShareCompared,
        description: summaryWidgets?.[WidgetId.summary_prepaid_share]?.tool_tip.primary_text,
        isPercentage: true
      }
    ]
  }, [summaryData, summaryWidgets])

  return (
    <div ref={ref} className='w-full h-full'>
      {/* Summary Stats */}
      <div
        id='summary-cards'
        className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4'
        data-track-id='summary-cards'
      >
        {summaryCards.map((card, index) => (
          <SummaryCard key={index} {...card} isLoading={isLoading} />
        ))}
      </div>
      {/* <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-6'>
        {footerCards.map((card, index) => (
          <FooterSummaryCard key={index} {...card} />
        ))}
      </div> */}
    </div>
  )
}

export default OverviewSection
